/**
 * Hyperliquid Queries
 *
 * This file contains TRPC v2 implementations for Hyperliquid functionality.
 * All hooks use the new `trpc.hyperliquid.*` pattern.
 */

import { useQuery } from '@tanstack/react-query';

import type { ExchangeKey } from '../../lib/exchanges';
import { getTRPCClient } from '../config';

/**
 * Query hook for fetching external actions (deposits/withdrawals)
 * @param exchange - Optional exchange filter (defaults to 'hyperliquid')
 * @param pagination - Optional pagination parameters (page and pageSize)
 * @param queryOptions - Optional React Query options for polling, refetch, etc.
 */
/**
 * Query hook for fetching the Hyperliquid unified account status of an address.
 *
 * The perps product is unified-account only, so funds deposited to an account
 * that is not in `unifiedAccount` mode won't surface there until the user
 * enables the unified account in the perps app.
 *
 * @param address - Address to check; the query is disabled while undefined
 */
export const useUnifiedAccountStatus = (address?: string) => {
  return useQuery({
    ...getTRPCClient().hyperliquid.getUnifiedAccountStatus.queryOptions({
      address: address ?? '',
    }),
    enabled: Boolean(address),
    staleTime: 30_000,
  });
};

export const useExternalActions = (
  exchange?: ExchangeKey,
  pagination?: { page?: number; pageSize?: number },
  queryOptions?: {
    refetchInterval?: number | false;
    staleTime?: number;
    refetchOnMount?: boolean;
    refetchOnWindowFocus?: boolean;
    refetchOnReconnect?: boolean;
  }
) => {
  return useQuery({
    ...getTRPCClient().hyperliquid.getExternalActions.queryOptions({
      protocol: exchange, // Backend still uses 'protocol' field name
      ...pagination,
    }),
    ...queryOptions,
  });
};
